import React, { useState, useRef, useEffect } from 'react';
import { useApi } from "../api/useApi";
import './IncidentForm.css';
import { getIncidentApiToken, getAuthApiToken } from '../api/tokenService';
import { useAuth } from '../context/AuthContext';
// IncidentForm.js - Add these imports at the top
import { useSubscriptionGuard } from '../hooks/useSubscriptionGuard';
import { trackUsage } from '../services/usageService';
import { extractTextFromFile } from '../utils/fileTextExtractor';
// IncidentForm.js - Add import at the top
import ExpertSelector from './ExpertSelector';

const IncidentForm = ({ onAnalysisComplete, onError, setLoading, apiUrl }) => {
  const { post, postWithIdToken } = useApi();
  const { user, updateUser } = useAuth(); // Add updateUser from AuthContext
  // Inside the IncidentForm component, add this after the existing hooks:
  const { subscription, isValid: subscriptionValid, isLoading: subLoading, error: subError, usage, validateSubscription } = useSubscriptionGuard();
  const [selectedExpert, setSelectedExpert] = useState('TechnicalIncidentExpert');
  const [formData, setFormData] = useState({
    incidentId: `INC-${Date.now().toString().slice(-6)}`,
    logs: '',
    alerts: '',
    metrics: ''
  });

  const [config, setConfig] = useState({
    logLimits: {
      maxRawLogsKB: 300,
      maxTokens: 50000
    },
    alertLimits: {
      maxAlerts: 50,
      groupBy: {
        service: true,
        errorType: true,
        timeWindow: true
      }
    },
    incidentDuration: {
      defaultWindowHours: 2,
      allowManualExpansion: true
    },
    currency: 'INR' // Default to INR
  });

  const [useSampleData, setUseSampleData] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [isLoadingCurrency, setIsLoadingCurrency] = useState(true);

  // File input refs
  const logsFileRef = useRef(null);
  const alertsFileRef = useRef(null);
  const metricsFileRef = useRef(null);

  // Currency options with symbols
  const currencyOptions = [
    { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
    { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  ];

  // Fetch user's currency from backend when component mounts
  useEffect(() => {
    const fetchUserCurrency = async () => {
      try {
        // If user is already in context with currency, use it
        if (user?.currency) {
          console.log('Using currency from user context:', user.currency);
          setConfig(prev => ({ ...prev, currency: user.currency }));
          setIsLoadingCurrency(false);
          return;
        }

        // If not in context, fetch from backend API
        const token = await getAuthApiToken();
        if (!token) {
          console.log('No token found, using default currency (INR)');
          setIsLoadingCurrency(false);
          return;
        }
        const authUrl = process.env.REACT_APP_AUTH_URL;
        const response = await fetch(`${authUrl}/api/auth/users/my-profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const userData = await response.json();
          if (userData.currency) {
            setConfig(prev => ({ ...prev, currency: userData.currency }));
            // Also update AuthContext if needed
            if (updateUser) {
              updateUser({ ...user, currency: userData.currency });
            }
          }
        } else {
          console.log('Failed to fetch user currency, using default INR');
        }
      } catch (error) {
        console.error('Error fetching user currency:', error);
      } finally {
        setIsLoadingCurrency(false);
      }
    };

    fetchUserCurrency();
  }, [user, apiUrl, updateUser]);

  // Update user's currency preference in backend and refresh auth context
  const updateUserCurrency = async (newCurrency) => {
    try {
      const token = await getAuthApiToken();
      const authUrl = process.env.REACT_APP_AUTH_URL;
      const response = await fetch(`${authUrl}/api/auth/users/update-currency`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ currency: newCurrency })
      });

      if (response.ok) {
        console.log('Currency updated in backend:', newCurrency);

        // Update the user object in AuthContext
        if (updateUser) {
          updateUser({ ...user, currency: newCurrency });
        }

        // Also update localStorage if user is stored there
        const storedUser = sessionStorage.getItem('user');
        if (storedUser) {
          const userObj = JSON.parse(storedUser);
          userObj.currency = newCurrency;
          sessionStorage.setItem('user', JSON.stringify(userObj));
        }

        // Show success message
        alert(`Currency preference updated to ${newCurrency}. Refresh the page to see changes in subscription plans.`);

        // Optional: Reload subscription plans if SubscriptionPlan component is mounted
        // You can dispatch a custom event or use a callback
        window.dispatchEvent(new CustomEvent('currencyChanged', { detail: { currency: newCurrency } }));

      } else {
        console.error('Failed to update currency:', response.status);
        alert('Failed to update currency preference. Please try again.');
      }
    } catch (error) {
      console.error('Error updating user currency:', error);
      alert('Error updating currency preference.');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleConfigChange = (section, field, value) => {
    setConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleCurrencyChange = async (currency) => {
    const oldCurrency = config.currency;
    setConfig(prev => ({ ...prev, currency }));

    // Save to backend and update context
    await updateUserCurrency(currency);

    // You can also add a reload or refresh here if needed
    // window.location.reload(); // Uncomment if you want to refresh the page
  };

  const handleGroupByChange = (field, checked) => {
    setConfig(prev => ({
      ...prev,
      alertLimits: {
        ...prev.alertLimits,
        groupBy: {
          ...prev.alertLimits.groupBy,
          [field]: checked
        }
      }
    }));
  };

  const handleFileUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    // File size validation (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      onError('File size exceeds 5MB limit');
      return;
    }

    try {
      const text = await extractTextFromFile(file);
      setFormData(prev => ({
        ...prev,
        [field]: text
      }));
    } catch (err) {
      onError(err.message || 'Could not read file. Please paste the text instead.');
    }
  };

  const loadSampleData = () => {
    setFormData({
      incidentId: 'CASCADE-FAILURE-2024-003',
      logs: `2024-01-15T10:00:00Z - ERROR: Database connection pool at 95% capacity
2024-01-15T10:02:00Z - WARN: Connection timeout for user service
2024-01-15T10:05:00Z - ERROR: Database connections exhausted
2024-01-15T10:07:00Z - CRITICAL: Service degradation detected
2024-01-15T10:15:00Z - INFO: Connection pool increased to 200
2024-01-15T10:20:00Z - INFO: Service recovery in progress`,
      alerts: `High Database Connection Usage (95%)
Service Response Time > 5000ms
Error Rate Spike Detected
Multiple Timeout Errors`,
      metrics: `{
  "cpu_usage": "85%",
  "memory_usage": "78%",
  "db_connections": "195/200",
  "error_rate": "45%",
  "response_time": "5200ms"
}`
    });

    setConfig(prev => ({
      ...prev,
      logLimits: {
        maxRawLogsKB: 250,
        maxTokens: 45000
      },
      alertLimits: {
        maxAlerts: 45,
        groupBy: {
          service: true,
          errorType: true,
          timeWindow: false
        }
      },
      incidentDuration: {
        defaultWindowHours: 1.5,
        allowManualExpansion: true
      }
    }));

    setUseSampleData(true);
  };

  const clearForm = () => {
    setFormData({
      incidentId: `INC-${Date.now().toString().slice(-6)}`,
      logs: '',
      alerts: '',
      metrics: ''
    });

    setConfig(prev => ({
      ...prev,
      logLimits: {
        maxRawLogsKB: 300,
        maxTokens: 50000
      },
      alertLimits: {
        maxAlerts: 50,
        groupBy: {
          service: true,
          errorType: true,
          timeWindow: true
        }
      },
      incidentDuration: {
        defaultWindowHours: 2,
        allowManualExpansion: true
      }
    }));

    // Clear file inputs
    if (logsFileRef.current) logsFileRef.current.value = '';
    if (alertsFileRef.current) alertsFileRef.current.value = '';
    if (metricsFileRef.current) metricsFileRef.current.value = '';

    setUseSampleData(false);
  };

  // Get currency symbol for display
  const getCurrencySymbol = (currencyCode) => {
    const currency = currencyOptions.find(c => c.code === currencyCode);
    return currency ? currency.symbol : '₹';
  };

  // Add this function before handleSubmit
  const validateBeforeAnalysis = async () => {
    // Check if subscription is valid
    if (!subscriptionValid) {
      if (subError?.type === 'EXPIRED') {
        onError(`⚠️ ${subError.message}`);
        // Optionally open subscription modal
        window.dispatchEvent(new CustomEvent('openSubscription'));
      } else if (subError?.type === 'LIMIT_REACHED') {
        onError(`⚠️ ${subError.message}`);
        window.dispatchEvent(new CustomEvent('openSubscription'));
      }
      return false;
    }
    return true;
  };

  // Add this function to handle expert selection
  const handleExpertSelect = (expertId) => {
    setSelectedExpert(expertId);
    console.log('Selected expert:', expertId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Add validation before proceeding
    const canProceed = await validateBeforeAnalysis();
    if (!canProceed) return;

    setLoading(true);
    onError(null);

    try {
      // Parse metrics if it's a JSON string
      let metrics = formData.metrics;
      try {
        if (formData.metrics.trim()) {
          metrics = JSON.parse(formData.metrics);
        }
      } catch (e) {
        // Keep as string if not valid JSON
      }

      const payload = {
        incident: {
          incidentId: formData.incidentId,
          logs: formData.logs.split('\n').filter(line => line.trim()),
          alerts: formData.alerts.split('\n').filter(line => line.trim()),
          metrics: metrics,
          promptName: selectedExpert, // ✅ Send selected expert as promptName
          agentConfig: {
            logLimits: config.logLimits,
            alertLimits: config.alertLimits,
            incidentDuration: config.incidentDuration,
            preferredCurrency: config.currency // Send user's currency to backend
          }
        }
      };

      console.log('Sending to API:', `${apiUrl}/api/v1/analyze`);
      console.log('With currency:', config.currency);
      const idToken = await getIncidentApiToken();
      const response = await postWithIdToken(`${apiUrl}/api/v1/analyze`, payload, idToken);
      const result = response;

      // Handle both response structures
      let analysisData = result;
      if (result.success && result.data) {
        analysisData = result.data;
      }

      // Normalize recommendations (keep objects intact so priority/action survive for color-coded display)
      if (analysisData.recommendations && Array.isArray(analysisData.recommendations)) {
        analysisData.recommendations = analysisData.recommendations.map(rec =>
          typeof rec === 'string' ? rec : rec
        );
      }

      await trackUsage('incident');
      await validateSubscription();
      onAnalysisComplete(result);
    } catch (error) {
      console.error('Analysis error:', error);
      onError(error.message || 'Failed to analyze incident. Please check your backend connection.');
    } finally {
      setLoading(false);
    }
  };

  // Add a warning banner if subscription is expiring soon
  const showExpiryWarning = () => {
    if (subscription?.endDate && subscription.plan?.name !== 'free') {
      const daysUntilExpiry = Math.ceil((new Date(subscription.endDate) - new Date()) / (1000 * 60 * 60 * 24));
      if (daysUntilExpiry <= 7 && daysUntilExpiry > 0) {
        return (
          <div className="expiry-warning">
            ⚠️ Your subscription expires in {daysUntilExpiry} days.
            <button onClick={() => window.dispatchEvent(new CustomEvent('openSubscription'))}>
              Renew Now
            </button>
          </div>
        );
      }
    }
    return null;
  };

  // Add usage progress bar for free tier
  const showUsageProgress = () => {
    if ((!subscription || subscription.plan?.name === 'free') && usage) {
      const percentage = (usage.currentUsage / usage.limit) * 100;
      return (
        <div className="usage-progress">
          <div className="usage-info">
            <span>Free Plan Usage: {usage.currentUsage}/{usage.limit} incidents this month</span>
            {percentage >= 80 && (
              <button onClick={() => window.dispatchEvent(new CustomEvent('openSubscription'))}>
                Upgrade
              </button>
            )}
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${Math.min(percentage, 100)}%` }}></div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Show loading state while fetching currency
  if (isLoadingCurrency) {
    return (
      <div className="incident-form">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading your preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="incident-form">
      {showExpiryWarning()}
      {showUsageProgress()}
      <div className="form-header">
        <h2>📝 Incident Details</h2>
        <div className="form-actions-wrapper">
          <div className="form-actions-row">
            <ExpertSelector
              onSelectExpert={handleExpertSelect}
              selectedExpert={selectedExpert}
              disabled={false}
            />
          </div>
          <div className="form-actions-row">
            <button
              type="button"
              onClick={() => setShowConfig(!showConfig)}
              className="secondary-btn"
            >
              {showConfig ? 'Hide' : 'Show'} AI Agent Config
            </button>
            <button
              type="button"
              onClick={loadSampleData}
              className="secondary-btn"
            >
              Load Sample Data
            </button>
            <button
              type="button"
              onClick={clearForm}
              className="secondary-btn"
            >
              Clear Form
            </button>
          </div>
        </div>
      </div>

      {showConfig && (
        <div className="config-section">
          <h3>🤖 AI Agent Configuration</h3>
          <div className="config-grid">
            <div className="config-group">
              <h4>📄 Log Limits</h4>
              <div className="config-row">
                <label>
                  Max Raw Logs (KB):
                  <input
                    type="number"
                    value={config.logLimits.maxRawLogsKB}
                    onChange={(e) => handleConfigChange('logLimits', 'maxRawLogsKB', parseInt(e.target.value))}
                    min="50"
                    max="1000"
                  />
                </label>
                <label>
                  Max Tokens:
                  <input
                    type="number"
                    value={config.logLimits.maxTokens}
                    onChange={(e) => handleConfigChange('logLimits', 'maxTokens', parseInt(e.target.value))}
                    min="1000"
                    max="100000"
                  />
                </label>
              </div>
            </div>

            <div className="config-group">
              <h4>🚨 Alert Limits</h4>
              <div className="config-row">
                <label>
                  Max Alerts:
                  <input
                    type="number"
                    value={config.alertLimits.maxAlerts}
                    onChange={(e) => handleConfigChange('alertLimits', 'maxAlerts', parseInt(e.target.value))}
                    min="10"
                    max="200"
                  />
                </label>
                <div className="checkbox-group">
                  <span>Group by:</span>
                  <label>
                    <input
                      type="checkbox"
                      checked={config.alertLimits.groupBy.service}
                      onChange={(e) => handleGroupByChange('service', e.target.checked)}
                    />
                    Service
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={config.alertLimits.groupBy.errorType}
                      onChange={(e) => handleGroupByChange('errorType', e.target.checked)}
                    />
                    Error Type
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={config.alertLimits.groupBy.timeWindow}
                      onChange={(e) => handleGroupByChange('timeWindow', e.target.checked)}
                    />
                    Time Window
                  </label>
                </div>
              </div>
            </div>

            <div className="config-group">
              <h4>⏳ Incident Duration</h4>
              <div className="config-row">
                <label>
                  Default Window (hours):
                  <input
                    type="number"
                    step="0.5"
                    value={config.incidentDuration.defaultWindowHours}
                    onChange={(e) => handleConfigChange('incidentDuration', 'defaultWindowHours', parseFloat(e.target.value))}
                    min="0.5"
                    max="24"
                  />
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={config.incidentDuration.allowManualExpansion}
                    onChange={(e) => handleConfigChange('incidentDuration', 'allowManualExpansion', e.target.checked)}
                  />
                  Allow Manual Expansion
                </label>
              </div>
            </div>

            {/* Currency Selection - From User Table */}
            <div className="config-group">
              <h4>💱 Currency Preference</h4>
              <div className="config-row">
                <div className="currency-selector">
                  <select
                    value={config.currency}
                    onChange={(e) => handleCurrencyChange(e.target.value)}
                    className="currency-select"
                  >
                    {currencyOptions.map(currency => (
                      <option key={currency.code} value={currency.code}>
                        {currency.symbol} {currency.code} - {currency.name}
                      </option>
                    ))}
                  </select>
                  <span className="currency-preview">
                    💰 Cost estimates will be shown in {getCurrencySymbol(config.currency)}
                    {user?.currency && <span className="currency-source"> (Saved to your profile)</span>}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="incidentId">Incident ID *</label>
          <input
            type="text"
            id="incidentId"
            name="incidentId"
            value={formData.incidentId}
            onChange={handleChange}
            required
            placeholder="e.g., CASCADE-FAILURE-2024-003"
          />
        </div>

        <div className="form-group">
          <div className="field-header">
            <label htmlFor="logs">Logs</label>
            <input
              type="file"
              ref={logsFileRef}
              accept=".txt,.log,.json,.csv"
              onChange={(e) => handleFileUpload(e, 'logs')}
              className="file-upload"
            />
          </div>
          <textarea
            id="logs"
            name="logs"
            value={formData.logs}
            onChange={handleChange}
            rows="5"
            placeholder="Paste log entries (one per line) or upload file"
          />
          <div className="hint">Upload .txt, .log, .json, or .csv files (max 5MB)</div>
        </div>

        <div className="form-group">
          <div className="field-header">
            <label htmlFor="alerts">Alerts</label>
            <input
              type="file"
              ref={alertsFileRef}
              accept=".txt,.json,.csv"
              onChange={(e) => handleFileUpload(e, 'alerts')}
              className="file-upload"
            />
          </div>
          <textarea
            id="alerts"
            name="alerts"
            value={formData.alerts}
            onChange={handleChange}
            rows="4"
            placeholder="Alert messages (one per line) or upload file"
          />
        </div>

        <div className="form-group">
          <div className="field-header">
            <label htmlFor="metrics">Metrics (JSON format)</label>
            <input
              type="file"
              ref={metricsFileRef}
              accept=".json,.txt"
              onChange={(e) => handleFileUpload(e, 'metrics')}
              className="file-upload"
            />
          </div>
          <textarea
            id="metrics"
            name="metrics"
            value={formData.metrics}
            onChange={handleChange}
            rows="6"
            placeholder='{"metric": "value", "another": "value"} or upload JSON file'
          />
          <div className="hint">Upload .json file or enter JSON directly</div>
        </div>

        {useSampleData && (
          <div className="sample-data-notice">
            ⚠️ Using sample data. Modify fields as needed.
          </div>
        )}

        <button type="submit" className="submit-btn">
          🚀 Analyze with AI
        </button>
      </form>
    </div>
  );
};

export default IncidentForm;